import { type ValidationErrors } from 'final-form';
import type { TFunction } from 'i18next';
import {
  ADULTS,
  CAPTAINED,
  DEPARTURE_DATE_FIELD,
  DEPARTURE_TIME_FIELD,
  DURATION_FIELD,
  EMAIL,
  FIRST_NAME,
  MESSAGE_TO_OWNER,
  PHONE,
  TIME_STRING_FORMAT,
  type BookNowDirectFormState,
  type BookNowDirectFormStateSerialized,
  type FormStep,
} from 'src/book-now-direct/constants';
import { BASE_API_URI } from 'src/common/constants';
import { InstabookAvailability, type InstabookTripTime } from 'src/common/types/api/v4/instabook';
import type { Trip } from 'src/common/types/api/v4/trips';
import { apiRequest } from 'src/common/utils/apiFetch';
import ApiResponseError from 'src/common/utils/apiResponseError';
import asyncPhoneValidator from 'src/common/utils/asyncPhoneValidator';
import emailValidator from 'email-validator';
import newTZDate, { formatDate } from 'src/common/utils/dateHelpers';
import { eachMinuteOfInterval } from 'date-fns';
import { timeStringToDateUpdate } from 'src/common/helpers';

/**
 * If user, the patch will occur on formStep 'details' with values of FormStateDetails
 * If !user, the patch will occur on formStep 'guest' with values of  FormStateCombined
 */
export const patchInstabook = async (
  instabookId: InstabookAvailability['id'],
  values: BookNowDirectFormState,
): Promise<Trip> => {
  const response = await apiRequest(`${BASE_API_URI}instabook/trips/${instabookId}/book/`, {
    method: 'PATCH',
    body: JSON.stringify({
      is_direct_booking: true, // IMPORTANT differentiator between regular instabook
      adults: values.adults,
      pickup_time: values.departTime,
      inquiry_comment: values[MESSAGE_TO_OWNER] ?? '',
      occasions: values.occasion ? [Number(values.occasion)] : undefined,
      email: values.email,
      first_name: values.firstName,
      phone: values.phone,
    }),
    bypass401Handling: true,
  });

  if (response.ok) {
    return response.json();
  }
  throw new ApiResponseError(response);
};

/** For flexible times, returns depart times in increments of 15 minutes */
export const reduceTripTimes = (tripTimes: InstabookTripTime[]): string[] => tripTimes.reduce(
  (acc, { min_start_time, max_start_time }) => {
    if (min_start_time === max_start_time) {
    // fixed time
      if (!acc.includes(min_start_time)) {
        acc.push(min_start_time);
      }
    } else {
      // flexible times, push every 15 minutes interval between min and max
      eachMinuteOfInterval(
        {
          start: timeStringToDateUpdate(min_start_time),
          end: timeStringToDateUpdate(max_start_time),
        },
        {
          step: 15,
        },
      ).forEach((betweenDateTime) => {
        const betweenTimeString = formatDate(betweenDateTime, TIME_STRING_FORMAT);
        if (!acc.includes(betweenTimeString)) {
          acc.push(betweenTimeString);
        }
      });
    }
    return acc.sort();
  },
  [] as string[],
);

/**
 * The direct-instabook form does not force user to select a single instabookCard (like trip-cart).
 * Rather, it narrows available instabooks on date based on Duration, Time, and Captained.
 * This helper filters all instabooks on the selected date against form values.
 * For days with many overlapping instabooks, multiple instabooks could still match.
 * By default, if multiple instabooks match, it returns the cheapest.
 */
export const filterInstabooks = (
  allInstabooksOnDate?: InstabookAvailability[],
  values?: BookNowDirectFormState,
  selectCheapest: boolean = true,
): InstabookAvailability[] | undefined => {
  if (!allInstabooksOnDate || !values) {
    return undefined;
  }
  const matches = allInstabooksOnDate.filter((instabook) => {
    let match = true;
    if (values.duration && (instabook.trip_length !== values.duration)) {
      match = false;
    }
    const withFilledTimes = reduceTripTimes(instabook.instabook_trip_times);
    if (values.departTime && !(withFilledTimes.some(
      (times) => times === values.departTime,
    ))) {
      match = false;
    }
    if (values.captained !== undefined) {
      if (instabook.captained !== values.captained) {
        match = false;
      }
    }
    return match;
  });

  if (matches.length > 1 && selectCheapest) {
    return [matches.sort((a, b) => a.booking_total - b.booking_total)[0]];
  }
  return matches;
};

/** Extracts hashedId from http://dev.getmyboat.com/direct-booking/{hashId}/  */
export const hashedIdFromDirectBookingUrl = (directBookingUrl: Trip['direct_booking_url']): string => {
  const parts = directBookingUrl.split('/');
  return parts.slice(parts.length - 2, parts.length - 1)[0];
};

/**
 * Only occassion is optional.
 * Generic error message is shown above Submit button
 * Error fields will be emphasized in red border
 * */
export const validateBookNowDirectForm = (
  formStep: FormStep,
  t: TFunction<'book-now-direct', undefined>,
) => async (
  values: BookNowDirectFormState,
): Promise<ValidationErrors> => {
  const errors = {};
  // required values
  [
    CAPTAINED,
    ADULTS,
    DEPARTURE_DATE_FIELD,
    DURATION_FIELD,
    DEPARTURE_TIME_FIELD,
  ].forEach((key) => {
    if (values[key] === undefined) {
      errors[key] = true;
    }
  });

  // only if !user
  if (formStep === 'guest') {
    if (!values[EMAIL]) {
      errors[EMAIL] = t('guest-email-error-label');
    }

    if (values[EMAIL] && !emailValidator.validate(values[EMAIL])) {
      errors[EMAIL] = t('guest-email-error-label_invalid');
    }

    if (!values[FIRST_NAME]) {
      errors[FIRST_NAME] = t('guest-first-name-error-label');
    }
    if (values[PHONE]) {
      if (await asyncPhoneValidator(values[PHONE])) {
        errors[PHONE] = t('guest-phone-error-label');
      }
    } else {
      errors[PHONE] = t('guest-phone-error-label');
    }
  }
  return errors;
};

/** Transform data from serialized JSON props or sessionData  */
export const parseSerializedFormState = (
  serializedFormState: BookNowDirectFormStateSerialized,
): BookNowDirectFormState => ({
  ...serializedFormState,
  [DEPARTURE_DATE_FIELD]: serializedFormState[DEPARTURE_DATE_FIELD]
    ? newTZDate(serializedFormState[DEPARTURE_DATE_FIELD])
    : undefined,
});
